import { useParams } from "react-router-dom";
import React, { useState } from "react";
import altelis_logo from "../../assets/images/altelis-full.svg";
import not_found from "../../assets/images/order-404.svg";
import valid from "../../assets/images/valid-subscription.png";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "react-toastify";
import toastOptions from "../../assets/constants/toast";
import CountdownTimer from "./CountDown";
import { LinkIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import CustomTag from "../../ui/CustomTag";
import "../../assets/css/pages/tests/test-submit.css";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Paris");

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const TestSubmit = () => {
    const { testId, userId } = useParams();
    const [refresh, setRefresh] = useState(0);
    const [file, setFile] = useState();
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [test, setTest] = useState();
    const [user, setUser] = useState();
    const [isFileValid, setIsFileValid] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [isSubmitting] = useState(false);

    React.useEffect(() => {
        setIsLoaded(false);
        axios
            .get(process.env.REACT_APP_UPA_API_HOST + "tests/" + testId + "/" + userId)
            .then((response) => {
                if (response.data) {
                    const calculatedStopDate = dayjs
                        .utc(response.data.startDate)
                        .add(response.data.test?.hours || 0, "hour")
                        .add(response.data.test?.minutes || 0, "minute");

                    setTest(response.data.test);
                    setUser({
                        ...response.data,
                        stopDate: calculatedStopDate.toISOString(),
                    });
                } else {
                    setError(true);
                }
                setIsLoaded(true);
            })
            .catch(() => {
                setError(true);
                setIsLoaded(true);
            });
    }, [refresh, testId, userId]);

    const handleRemaining = (remaining) => {
        if (remaining < 0) {
            setRefresh(refresh + 1);
        }
    };

    const handleFileChange = (selectedFile) => {
        if (!selectedFile) {
            setIsFileValid(false);
            setFile(null);
            return;
        }
        const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
        if (fileExtension !== "zip") {
            toast.error("Seuls les fichiers .zip sont acceptés", toastOptions);
            setIsFileValid(false);
            setFile(null);
            return;
        }
        if (selectedFile.size > MAX_FILE_SIZE) {
            toast.error("Le fichier est trop volumineux. Limite : 10 Mo", toastOptions);
            setIsFileValid(false);
            setFile(null);
            return;
        }
        setFile(selectedFile);
        setIsFileValid(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            handleFileChange(droppedFiles[0]);
        }
    };

    const handleInputChange = (e) => {
        const selectedFile = e.target.files[0];
        handleFileChange(selectedFile);
    };

    const submitFile = () => {
        if (file && isFileValid) {
            const formData = new FormData();
            formData.append("file", file);
            fetch(`${process.env.REACT_APP_UPA_API_HOST}tests/${testId}/${userId}`, {
                method: "POST",
                body: formData,
            })
                .then((response) => {
                    const contentType = response.headers.get("content-type") || "";
                    if (!response.ok) {
                        if (contentType.includes("application/json")) {
                            return response.text().then((text) => {
                                let json = {};
                                try {
                                    json = text ? JSON.parse(text) : {};
                                } catch {
                                    throw new Error("Erreur lors du traitement de la réponse");
                                }
                                throw new Error(json.message || "Une erreur est survenue. Veuillez vérifier l'API.");
                            });
                        } else {
                            throw new Error("Une erreur est survenue. Veuillez vérifier l'API.");
                        }
                    }
                    if (contentType.includes("application/json")) {
                        return response.text().then((text) => (text ? JSON.parse(text) : { message: "Fichier téléchargé avec succès." }));
                    }
                    return { message: "Fichier téléchargé avec succès." };
                })
                .then((data) => {
                    toast.success(data.message || "Votre fichier a bien été déposé", toastOptions);
                    setFile(null);
                    setIsFileValid(false);
                    setRefresh(refresh + 1);
                })
                .catch((error) => {
                    toast.error(error.message || "Erreur lors du dépôt du fichier", toastOptions);
                });
        }
    };

    return isLoaded ? (
        !error ? (
            <div className="test-submit">
                <div className="test-submit__main">
                    <div className="test-submit__left">
                        <div className="test-submit__left-content">
                            <h1>{test.name}</h1>
                            <p>Vous êtes inscrit(e) à ce test de candidature. Suivez les instructions pour le compléter avec succès.</p>
                            <div className="test-submit__user-badge">
                                {user.firstName} {user.lastName.toUpperCase()}
                            </div>
                        </div>
                    </div>
                    <div className="test-submit__right">
                        <div className="test-submit__header">
                            <img src={altelis_logo} alt="Altelis" className="test-submit__logo" />
                            <div className="test-submit__contact-info">
                                <CustomTag value="phone" label="01 85 09 74 06" />
                                <CustomTag value="email" label="candidature@altelis.com" />
                            </div>
                        </div>

                        {dayjs().tz("Europe/Paris") < dayjs(user.startDate).tz("Europe/Paris") ? (
                            <div className="test-submit__upcoming-content">
                                <div className="test-submit__greeting">
                                    <h1>Bonjour {user.firstName} !</h1>
                                </div>

                                <div className="test-submit__test-info">
                                    <p>
                                        Votre test est prévu le <strong>{dayjs(user.startDate).tz("Europe/Paris").format("dddd DD MMMM à HH:mm")}</strong> pour une durée de{" "}
                                        {test.hours > 0 && `${test.hours} heure${test.hours > 1 ? "s" : ""}`}
                                        {test.hours > 0 && test.minutes > 0 && " et "}
                                        {test.minutes > 0 && `${test.minutes} minute${test.minutes > 1 ? "s" : ""}`}.
                                    </p>
                                    <p>Le sujet sera disponible sur cette page dès le début de la période du test.</p>
                                </div>

                                <div className="test-submit__countdown">
                                    <div className="test-submit__countdown-title">Votre test commence dans :</div>
                                    <CountdownTimer targetDate={dayjs(user.startDate).valueOf()} introText="" setRemaining={handleRemaining} />
                                </div>
                            </div>
                        ) : user.submittedAt ? (
                            <div className="test-submit__finished">
                                <div className="test-submit__success-icon">
                                    <img src={valid} alt="Test terminé" width={80} />
                                </div>
                                <div className="test-submit__finished-content">
                                    <h1>Test terminé !</h1>
                                    <p>Merci pour votre implication ! Nous reviendrons vers vous au plus vite.</p>
                                    <div className="test-submit__team">
                                        <strong>L'équipe Altelis</strong>
                                    </div>
                                </div>
                            </div>
                        ) : dayjs().tz("Europe/Paris") >= dayjs(user.startDate).tz("Europe/Paris") && dayjs().tz("Europe/Paris") <= dayjs(user.stopDate).tz("Europe/Paris") ? (
                            <div className="test-submit__active-content">
                                <div className="test-submit__greeting">
                                    <h1>Bonne chance {user.firstName} !</h1>
                                </div>

                                <div className="test-submit__countdown">
                                    <div className="test-submit__countdown-title">Votre test se termine dans :</div>
                                    <CountdownTimer targetDate={dayjs(user.stopDate).valueOf()} displayDays={false} introText="" setRemaining={handleRemaining} />
                                </div>

                                <div className="test-submit__actions">
                                    <a href={test.subjectLink} target="_blank" rel="noreferrer" className="button-large blue-button">
                                        <LinkIcon size={16} weight="bold" />
                                        <span>Accéder au sujet</span>
                                    </a>
                                </div>

                                <div className="test-submit__reminder">
                                    <p>
                                        <strong>Rappel :</strong> Seuls les fichiers compressés au format <strong>.zip</strong> sont acceptés. Si vous rencontrez des difficultés, contactez-nous à
                                        candidature@altelis.com
                                    </p>
                                </div>

                                <div className={`test-submit__file-upload ${dragActive ? "active" : ""}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                                    <label htmlFor="file">
                                        {!file ? "Glissez ou sélectionnez votre fichier .zip" : file.name}
                                    </label>
                                    <input accept=".zip" type="file" name="file" id="file" onChange={handleInputChange} />
                                    <p className="test-submit__file-size">(Taille maximale : 10 Mo)</p>
                                </div>

                                <div className="test-submit__upload-actions">
                                    <button className="button-large blue-button" onClick={submitFile} disabled={!isFileValid || isSubmitting}>
                                        <PaperPlaneTiltIcon size={16} weight="bold" />
                                        <span>Envoyer mon fichier</span>
                                    </button>
                                </div>

                                {user.submittedAt && (
                                    <div className="test-submit__success-message">
                                        <p>Fichier déposé avec succès à {dayjs(user.submittedAt).tz("Europe/Paris").format("HH:mm:ss")}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="test-submit__finished">
                                <div className="test-submit__success-icon">
                                    <img src={valid} alt="Test terminé" width={80} />
                                </div>
                                <div className="test-submit__expired-content">
                                    <h1>Créneau expiré</h1>
                                    <p>
                                        Vous aviez jusqu'à <strong>{dayjs(user.stopDate).tz("Europe/Paris").format("HH:mm")}</strong> pour effectuer le test.
                                    </p>
                                    <p>
                                        Veuillez contacter <a href="mailto:candidature@altelis.com">candidature@altelis.com</a> pour reprogrammer.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        ) : (
            <div className="test-submit">
                <div className="test-submit__main">
                    <div className="test-submit__left">
                        <div className="test-submit__left-content">
                            <h1>Erreur</h1>
                            <p>Une erreur s'est produite lors du chargement du test.</p>
                        </div>
                    </div>
                    <div className="test-submit__right">
                        <div className="test-submit__error-content">
                            <img src={not_found} alt="Test introuvable" width={200} />
                            <h1>Oups...</h1>
                            <p>Le test que vous cherchez n'est pas disponible pour le moment. Merci de réessayer plus tard.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    ) : (
        <div className="test-submit">
            <div className="test-submit__main">
                <div className="test-submit__left">
                    <div className="test-submit__left-content">
                        <h1>Chargement...</h1>
                        <p>Préparation de votre test en cours</p>
                    </div>
                </div>
                <div className="test-submit__right" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress size={30} style={{ color: "var(--blue-500)" }} />
                </div>
            </div>
        </div>
    );
};

export default TestSubmit;
